import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosInstance from "../config/Axios";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import { handleResErrors } from "./utils";

/**
 * Unwrap a list endpoint to an ARRAY of rows.
 *
 * Every match-scheduler endpoint returns CompanyResource, and JsonResource
 * wrapping is ON project-wide (no withoutWrapping() anywhere), so the wire
 * shape is always `{data: [...]}`. Hooks that returned `r.data` raw handed
 * components the wrapper instead of the rows, and the two ways a component
 * can consume that both fail silently or fatally:
 *   - `x.length > 0`  -> undefined > 0 -> false -> list never renders
 *   - `x.map(...)`    -> TypeError     -> React 18 unmounts -> white page
 * Both shipped: the first killed the league picker, the second white-paged
 * the Live Now tab.
 *
 * Returns [] for anything unrecognised, so a shape change degrades to an
 * empty picker rather than taking the page down.
 */
export const rows = (r) => {
    const d = r?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
};

// ─── Scheduled matches list ─────────────────────────────────────────────────
export const useGetMatchSchedules = (params = {}) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["matchSchedules", params],
        () => axiosInstance.get("/match-scheduler", { params }).then((r) => r.data),
        {
            keepPreviousData: true,
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── Venue channels for the "which channel is this match on" picker ─────────
// Flattened across receivers by the backend, so the form needs no set-top-box
// step. Callers pass { retry: false } so a 402 from the hardware_control gate
// settles immediately and the picker can hide itself instead of retrying.
export const useGetMatchChannels = (options = {}) =>
    useQuery(
        ["matchSchedulerChannels"],
        () => axiosInstance.get("/match-scheduler/channels").then((r) => r.data),
        { staleTime: 60_000, ...options }
    );

// ─── Create match schedule ──────────────────────────────────────────────────
export const useCreateMatchSchedule = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post("/match-scheduler/create", payload).then((r) => r.data),
        {
            onSuccess: () => {
                qc.invalidateQueries("matchSchedules");
                showToast("success", "Match Scheduler", "Match schedule created successfully!");
            },
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── Delete match schedule ──────────────────────────────────────────────────
export const useDeleteMatchSchedule = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (id) => axiosInstance.delete(`/match-scheduler/${id}/delete`).then((r) => r.data),
        {
            onSuccess: () => {
                qc.invalidateQueries("matchSchedules");
                showToast("success", "Match Scheduler", "Schedule removed!");
            },
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── Manual trigger ─────────────────────────────────────────────────────────
export const useTriggerMatchSchedule = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (id) => axiosInstance.post(`/match-scheduler/${id}/trigger`).then((r) => r.data),
        {
            onSuccess: () => {
                qc.invalidateQueries("matchSchedules");
                showToast("success", "Match Scheduler", "Campaign assignment triggered!");
            },
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── API-Football: upcoming fixtures ────────────────────────────────────────
export const useUpcomingFixtures = (leagueId, season, enabled = false) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["upcomingFixtures", leagueId, season],
        () => axiosInstance.get("/match-scheduler/fixtures/upcoming", {
            params: { league_id: leagueId, season },
        }).then(rows),
        {
            enabled: enabled && !!leagueId && !!season,
            staleTime: 5 * 60 * 1000,
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── API-Football: live fixtures ─────────────────────────────────────────────
export const useLiveFixtures = () => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["liveFixtures"],
        () => axiosInstance.get("/match-scheduler/fixtures/live").then(rows),
        {
            refetchInterval: 60 * 1000,  // refresh every minute for live matches
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── API-Football: search leagues ────────────────────────────────────────────
export const useSearchLeagues = (name) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["searchLeagues", name],
        () => axiosInstance.get("/match-scheduler/leagues/search", {
            params: { name },
        }).then(rows),
        {
            enabled: name?.length >= 2,
            staleTime: 60 * 60 * 1000,
            onError: (err) => handleResErrors(err, showToast),
        }
    );
};

// ─── Popular leagues — curated list from API-Football ───────────────────────
// Un-gated and cached 24h server-side. Errors are swallowed on purpose: this
// is the page's landing request, and consumers render an empty-state message
// rather than a toast on first paint.
export const usePopularLeagues = () => {
    return useQuery(
        ["popularLeagues"],
        () => axiosInstance.get("/match-scheduler/leagues/popular").then(rows),
        {
            staleTime: 24 * 60 * 60 * 1000,
            retry: false,
            // No onError toast — popular leagues are a no-op fallback list; a
            // 402/503 here would surprise the user on first page load.
        }
    );
};

// ─── Fixture discovery by league (API-Football) ─────────────────────────────
// Season is optional on the wire: the backend resolves the league's CURRENT
// season when it is omitted, so the Index's landing list needs no season UI.
// Pass one explicitly (FixtureBrowser does) to browse a past season.
//
// `retry: false` is deliberate — a 402 (feature gate) or 503 must surface at
// once rather than after three silent attempts.
export const useFixturesByLeague = (leagueId, season) => {
    return useQuery(
        ["fixtures", leagueId, season ?? "current"],
        () => axiosInstance
            .get("/match-scheduler/fixtures", {
                params: season ? { league_id: leagueId, season } : { league_id: leagueId },
            })
            .then(rows),
        {
            enabled: !!leagueId,
            staleTime: 30 * 60 * 1000,   // mirrors the backend DISCOVERY_TTL
            retry: false,
        }
    );
};

// ─── Season options for a league (API-Football) ─────────────────────────────
// Replaces a hardcoded [2025, 2024, 2023] in FixtureBrowser that froze the UI
// to seasons the vendor had already moved past — the day 2026 opened, every
// fixture in it was unreachable. Returns { seasons: [...], current: n }.
export const useLeagueSeasons = (leagueId) => {
    return useQuery(
        ["leagueSeasons", leagueId],
        () => axiosInstance
            .get(`/match-scheduler/leagues/${leagueId}/seasons`)
            .then((r) => r.data?.data ?? r.data ?? {}),
        {
            enabled: !!leagueId,
            staleTime: 24 * 60 * 60 * 1000,
            retry: false,
        }
    );
};
